/* eslint-disable eslint-comments/no-restricted-disable */
/* eslint-disable @atlassian/react-entrypoint/no-imports-in-entrypoint-file */
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { getProjectKey } from '@jira/router-resources__utils/src/getProjectKeyId.tsx';
import type { EntryPoint, EntryPointComponent } from 'react-relay';

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

import Query, { type L1MenuItemsQuery } from './ui/__generated__/L1MenuItemsQuery.graphql';
import { invitedAppShortcutsRecommendationsEntryPoint } from './ui/configurable-menu-items/global-shortcuts/invited-app-shortcuts-recommendations/invitedAppShortcutsRecommendations.entrypoint.tsx';

// Result matches the generics of the async component's EntryPointProps
// except for TComponentProps which is derived from `root`
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type EntryPointParams = EntryPointRouteParams & any;

type InvitedAppShortcutsRecommendationsEntryPointType =
	typeof invitedAppShortcutsRecommendationsEntryPoint;

// Optional `invitedAppShortcutsRecommendations` child entrypoint: present only
// when the user is authenticated AND both productionisation gates are on.
// Render dispatcher in `GlobalShortcutsComponent` falls back when absent.
type Nav4SidebarContentCoreEntryPointComponent = EntryPointComponent<
	{ nav4SidebarContentCore: L1MenuItemsQuery },
	{ invitedAppShortcutsRecommendations?: InvitedAppShortcutsRecommendationsEntryPointType },
	{},
	{}
>;

export const nav4SidebarContentCoreEntryPoint: EntryPoint<
	Nav4SidebarContentCoreEntryPointComponent,
	EntryPointParams
> = createEntryPoint({
	// sidebar is critical to rendering the initial view of a page
	root: JSResourceForUserVisible<Nav4SidebarContentCoreEntryPointComponent>(
		/* webpackChunkName: "nav4-sidebar-content-core-entry-point" */ './index.tsx',
		{ includeInShellOnlySsrBundle: true, entryFsSsrLiftImportToModule: true },
	),
	getPreloadProps(params: EntryPointParams) {
		const {
			context,
			tenantContext,
			entryPoints = {},
			isAppsExpanded,
			isFiltersExpanded,
			isPlansExpanded,
			isProjectsExpanded,
			isOperationsExpanded,
		} = params;
		// The two parameters have been injected by the theme setter, and should
		// not be here. Setting them naively just to be able to use the sidebar entryPoint again.
		const projectAri = '';
		let projectKey = '';
		if (context.match.params?.projectKey) {
			projectKey = context.match.params.projectKey;
		} else if (context.match.params?.issueKey && fg('move-issue-view-to-entrypoint')) {
			projectKey = getProjectKey(context.match, context.query);
		}

		// Preload the productionisation child entrypoint for authed users in the
		// test cohort. Read the experiment via `UNSAFE_noExposureExp` so control
		// (`isEnabled: false`) skips the extra entrypoint WITHOUT firing an
		// exposure here — the exposure fires in
		// `GlobalShortcutsWithInvitedAppRecommendations.tsx` once recs render.
		const mergedEntryPoints =
			// eslint-disable-next-line jira/ff/no-preconditioning -- kill-switch gate + cohort read
			!tenantContext.isAnonymous &&
			fg('twcg-460-preload-grs-invited-app-shortcuts-gate') &&
			UNSAFE_noExposureExp('twcg-460-preload-grs-invited-app-shortcuts')[0].get('isEnabled', false)
				? {
						...entryPoints,
						invitedAppShortcutsRecommendations: {
							entryPoint: invitedAppShortcutsRecommendationsEntryPoint,
							entryPointParams: { cloudId: tenantContext.cloudId },
						},
					}
				: entryPoints;

		return {
			// Queries your async component uses
			queries: {
				nav4SidebarContentCore: {
					// generated .graphql
					parameters: Query,
					// variables for your query
					variables: {
						cloudId: tenantContext.cloudId,
						isAppsExpanded,
						isFiltersExpanded,
						isPlansExpanded,
						isProjectsExpanded,
						isOperationsExpanded,
						projectAri,
						projectKey,
						entityAriPlans: `ari:cloud:jira:${tenantContext.cloudId}:navigation-settings/activation/${tenantContext.activationId}/plans`,
						entityAriFilters: `ari:cloud:jira:${tenantContext.cloudId}:navigation-settings/activation/${tenantContext.activationId}/filters`,
						entityAriApps: `ari:cloud:jira:${tenantContext.cloudId}:navigation-settings/activation/${tenantContext.activationId}/apps`,
						...(fg('jpd-enable-roadmaps-recents-fe')
							? {
									entityAriRoadmaps: `ari:cloud:jira:${tenantContext.cloudId}:navigation-settings/activation/${tenantContext.activationId}/collections`,
								}
							: {}),
						ownerAri:
							tenantContext.atlassianAccountId &&
							`ari:cloud:identity::user/${tenantContext.atlassianAccountId}`,
						isAnonymous: tenantContext.isAnonymous,
						isRoadmapUpsellEnabled: fg('jpd-roadmaps-in-jira'),
					},
					// fetch options
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
				},
			},
			entryPoints: mergedEntryPoints,
		};
	},
});
